import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loader2, Users, ArrowRight } from "lucide-react";

export default function JoinPod() {
  const { podId } = useParams();
  const { user, loading, refresh } = useAuth();
  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [joined, setJoined] = useState(null);
  const tried = useRef(false);

  useEffect(() => {
    api.get(`/join/${podId}`).then((r) => setInfo(r.data)).catch(() => setNotFound(true));
  }, [podId]);

  useEffect(() => {
    if (loading || !user || !info || tried.current) return;
    tried.current = true;
    localStorage.removeItem("pending_join");
    api.post(`/join/${podId}`).then(async (r) => { await refresh(); setJoined(r.data); }).catch(() => setNotFound(true));
  }, [loading, user, info, podId]);

  const handleJoin = () => {
    localStorage.setItem("pending_join", podId);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="join-page">
      <div className="max-w-md w-full bg-card border border-border rounded-2xl p-8 space-y-6 text-center">
        <img src="/logo-trim.png" alt="Bouquet" className="h-14 w-32 object-contain rounded-lg mx-auto" />
        {notFound ? (
          <div className="space-y-3">
            <p className="font-display text-2xl font-medium" data-testid="join-not-found">This invite doesn't work anymore</p>
            <p className="text-sm text-muted-foreground">Ask your person to send a fresh invite from their pod page.</p>
          </div>
        ) : joined ? (
          <div className="space-y-4">
            <p className="font-display text-2xl font-medium" data-testid="join-success">You're in "{joined.pod_name}"</p>
            <p className="text-sm text-muted-foreground">Welcome to Bouquet. Your pod is waiting for you.</p>
            <Link to={`/pods/${joined.pod_id}`} data-testid="join-goto-pod"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full text-sm font-semibold hover:opacity-90 transition-opacity">
              Open your pod <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ) : info ? (
          user ? (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground" data-testid="join-processing">
              <Loader2 className="h-4 w-4 animate-spin" /> Adding you to {info.name}…
            </div>
          ) : (
            <div className="space-y-5">
              <div className="space-y-2">
                <div className="h-10 w-10 rounded-full bg-sage-pale mx-auto flex items-center justify-center">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <p className="font-display text-2xl font-medium" data-testid="join-invite-title">You're invited to "{info.name}"</p>
                {info.description && <p className="text-sm text-muted-foreground">{info.description}</p>}
                <p className="text-xs text-muted-foreground">{info.member_count} {info.member_count === 1 ? "person" : "people"} growing together</p>
              </div>
              <button data-testid="join-google-button" onClick={handleJoin}
                className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground px-6 py-3.5 rounded-full text-sm font-semibold hover:opacity-90 transition-opacity">
                Continue with Google to join <ArrowRight className="h-4 w-4" />
              </button>
              <p className="text-xs text-muted-foreground">New to Bouquet? This signs you up and adds you to the pod in one step.</p>
              <button data-testid="join-existing-member" onClick={handleJoin}
                className="text-xs text-primary font-medium hover:underline">Already on Bouquet? Sign in — it works the same.</button>
            </div>
          )
        ) : (
          <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto" />
        )}
      </div>
    </div>
  );
}
